import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { NgIf } from '@angular/common';
import { RuntimeConfigService } from '../../../services/common/runtime-config.service';

/**
 * The `*` shown beside a mandatory field's label. **One marker for every sheet.**
 *
 * Usage — always glued to the label text with NO whitespace before it:
 * ```html
 * <label class="field-label">
 *   {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
 * </label>
 * ```
 *
 * Works in a `<label>`, a `<td>` row label, and a `<th>` column header alike,
 * which is what the five idioms it replaces could not do — the marker used to
 * live inside `<label class="field-label">`, so the seven sheets that render
 * their fields as table rows had nowhere to put it and simply went unmarked for
 * ten months. Design rules: `.claude/rules/required-marker.md`.
 *
 * ## Two layout traps this closes by construction
 *
 * 1. **The marker must not wrap onto its own line.** A source newline between
 *    the label text and the marker collapses to a space, and that space is the
 *    only line-break opportunity before the marker — the browser then breaks
 *    there and drops a lone `*` onto the next line. Hence "glued, no
 *    whitespace"; the visual gap comes from `margin-left`, which is NOT a break
 *    opportunity. (Write `{{ label }}<app-required-marker …/>`, not the marker
 *    on the next indented line.)
 * 2. **`display: inline` on the host is load-bearing.** The old icon idiom used
 *    PrimeNG's `<i class="pi pi-asterisk">`, and `.pi` is `inline-block` — an
 *    atomic inline whose box boundary is a break opportunity on BOTH sides
 *    regardless of whitespace, so the icon could wrap away alone even when
 *    glued. That needed a hand-written `display: inline` override per context.
 *    A component host is `inline` by default and the marker is a plain `<span>`,
 *    so the glyph sits in the label's own text run and wraps with the last word.
 *    `:host { display: inline }` below states it explicitly rather than relying
 *    on the default.
 *
 * ## The global switch
 *
 * `mandatoryMarkersEnabled` (runtime config) suppresses EVERY mandatory `*` in
 * the app when `false`, which is the committed default on all four templates
 * (PO, 2026-08-11). Because all 17 sheets render their marker through this one
 * component, this is the only gate needed — there are no literal `*` glyphs
 * left on any label, row label, or column header.
 *
 * It is deliberately NOT wired into `isControlMandatory` or
 * `isMandatoryForValidation`: the fields remain mandatory and still block
 * submit, and a PO reversal must not require re-deriving which fields those
 * are. Flip the config value; nothing else.
 *
 * Note this makes the component DI-dependent. `RuntimeConfigService`
 * `requireLoaded()` throws before boot resolves, so a spec that RENDERS a
 * marker must provide the service (see `required-marker.component.spec.ts`).
 *
 * ## Styling
 *
 * Self-sufficient — colour, weight and gap are set here, not inherited from a
 * per-sheet rule, so the marker looks identical on every sheet. `#ef4444` +
 * `font-weight: 700` is the treatment the majority of sheets already shipped
 * (`.required-mark`); role 23's near-miss `#dc2626` was folded into it. The
 * class name `required-mark` is kept so the pre-existing
 * `::ng-deep .required-mark` rule in the filing-form container and the
 * keep-with-last-word rules in `styles.css` continue to match rather than
 * fighting this component.
 *
 * ## Accessibility
 *
 * The glyph is deliberately NOT `aria-hidden`. Angular does not emit
 * `aria-required` from `Validators.required`, so the `*` is currently the only
 * signal a screen reader gets; hiding it would remove information the span
 * idiom already announced on roles 0/1/2/7. If `aria-required` is ever wired up
 * on the inputs, revisit this and hide the glyph then.
 */
@Component({
  selector: 'app-required-marker',
  standalone: true,
  imports: [NgIf],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<span *ngIf="markersEnabled() && when" class="required-mark">*</span>`,
  styles: [
    `
      :host {
        display: inline;
      }

      .required-mark {
        color: #ef4444;
        font-weight: 700;
        margin-left: 0.25rem;
      }
    `,
  ],
})
export class RequiredMarkerComponent {
  private readonly runtimeConfig = inject(RuntimeConfigService);

  /**
   * The global kill switch — `mandatoryMarkersEnabled` in runtime config.
   * `false` on every committed template (PO, 2026-08-11). Presentation only:
   * mandatory fields still block submit via `isMandatoryForValidation`.
   */
  readonly markersEnabled = this.runtimeConfig.mandatoryMarkersEnabled;

  /** Render the `*` when true. Callers pass their `isFieldRequired(control)`. */
  @Input() when: boolean | null | undefined = false;
}
